import React from 'react';
import { Button, Modal, Table, Progress, Badge, Space, Spin, Typography } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { LinkWithToken as Link } from '../LinkWithToken';
import transcript_pb from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import transcript_pb_grpc, { ResponseStream, Status } from '@gen/proto/lr/transcript/v1alpha1/transcript_pb_service';
import { TRANSCRIPT_SERVICE_HOST } from '../config';
import { AuthContextProps, withAuth } from '../LocalAuthWrapper';
import { grpc } from '@improbable-eng/grpc-web';
import LanguageCode from './LanguageCode';
import ErrorExplain from './ErrorExplain';
import './JobsSpinner.css';

dayjs.extend(LocalizedFormat);

const { Title } = Typography;

const friendlyTranscriptJobState = (state: transcript_pb.TranscriptJob.State) => {
  switch (state) {
    case transcript_pb.TranscriptJob.State.SUBMITTED:
      return 'Sent inn';
    case transcript_pb.TranscriptJob.State.WAITING_FOR_CONTENT:
      return 'Beðið eftir gögnum';
    case transcript_pb.TranscriptJob.State.PROCESSING:
      return 'Í greiningu';
    case transcript_pb.TranscriptJob.State.SUCCESS:
      return 'Lokið';
    case transcript_pb.TranscriptJob.State.ERROR:
      return 'Villa';
    default:
      return 'Óþekkt';
  }
};

type JobsSpinnerState = {
  successList: transcript_pb.TranscriptJob.AsObject[];
  errorsList: transcript_pb.TranscriptJob.AsObject[];
  ongoingList: transcript_pb.TranscriptJob.AsObject[];
  listToDisplay: transcript_pb.TranscriptJob.AsObject[];
  clickedButton?: string;
  visible?: boolean;
};

type JobsSpinnerProps = {
  anonymousAccess?: boolean;
} & AuthContextProps;

class JobsSpinner extends React.Component<JobsSpinnerProps, JobsSpinnerState> {
  state: JobsSpinnerState = {
    successList: [],
    errorsList: [],
    ongoingList: [],
    listToDisplay: [],
    clickedButton: '',
    visible: false,
  };
  client: transcript_pb_grpc.TranscriptServiceClient;
  stream?: ResponseStream<transcript_pb.WatchTranscriptJobsResponse>;

  createClient = () =>
    new transcript_pb_grpc.TranscriptServiceClient(TRANSCRIPT_SERVICE_HOST, {
      transport: grpc.WebsocketTransport() /* grpc.WebsocketTransport() */,
    });

  constructor(props: JobsSpinnerProps) {
    super(props);
    this.client = this.createClient();
  }

  componentDidMount() {
    if (this.props.authCtx.accessToken) this.watchTranscriptJobs();
  }

  componentDidUpdate(prevProps: JobsSpinnerProps, _prevState: JobsSpinnerState) {
    if (!prevProps.authCtx.accessToken && this.props.authCtx.accessToken) {
      this.watchTranscriptJobs();
    } else if (prevProps.authCtx.accessToken && !this.props.authCtx.accessToken) {
      this.stream && this.stream.cancel();
    }
  }

  componentWillUnmount() {
    this.stream && this.stream.cancel();
  }

  updateSpinner = (jobs: transcript_pb.TranscriptJob.AsObject[]) => {
    /**
     * Updates the Spinners when new changes are detected in WatchTranscriptJobs
     */
    jobs.forEach((job) => {
      const jobName = job.name;
      if (
        job.state === transcript_pb.TranscriptJob.State.SUBMITTED ||
        job.state === transcript_pb.TranscriptJob.State.PROCESSING ||
        job.state === transcript_pb.TranscriptJob.State.WAITING_FOR_CONTENT
      ) {
        this.setState((prevState) => ({
          ongoingList: [...prevState.ongoingList.filter((job) => job.name !== jobName), job].filter((val, idx, arr) =>
            arr[idx - 1] ? val.name !== arr[idx - 1].name : true,
          ),
        }));
      } else if (job.state === transcript_pb.TranscriptJob.State.SUCCESS) {
        this.setState((prevState) => ({
          ongoingList: [...prevState.ongoingList.filter((job) => job.name !== jobName)],
          successList: [...prevState.successList, job].filter((val, idx, arr) =>
            arr[idx - 1] ? val.name !== arr[idx - 1].name : true,
          ),
        }));
      } else {
        this.setState((prevState) => ({
          ongoingList: [...prevState.ongoingList.filter((job) => job.name !== jobName)],
          errorsList: [...prevState.errorsList, job].filter((val, idx, arr) =>
            arr[idx - 1] ? val.name !== arr[idx - 1].name : true,
          ),
        }));
      }
    });
  };

  handleShowDetails = (listType: 'errors' | 'success' | 'ongoing') => {
    let detailsList: transcript_pb.TranscriptJob.AsObject[] = [];
    let listToEmpty: 'successList' | 'errorsList' | '' = '';

    if (listType === 'success') {
      detailsList = [...this.state.successList];
      listToEmpty = 'successList';
    } else if (listType === 'errors') {
      detailsList = [...this.state.errorsList];
      listToEmpty = 'errorsList';
    } else {
      // 'ongoing'
      detailsList = [...this.state.ongoingList];
      // listToEmpty remains '', so 'ongoingList' is not emptied
    }

    this.setState((prevState) => ({
      ...prevState,
      listToDisplay: detailsList,
      visible: true,
      clickedButton: listType,
      // Conditionally clear the list *only* if listToEmpty is set
      ...(listToEmpty && { [listToEmpty]: [] }),
    }));
  };

  handleHideDetails = () => {
    this.setState({ visible: !this.state.visible });

    if (this.state.clickedButton !== 'ongoing') {
      this.state.listToDisplay.forEach((job) => {
        this.acknowledgeTranscriptJob(job);
      });
    }
  };

  acknowledgeTranscriptJob = (job: transcript_pb.TranscriptJob.AsObject) => {
    const req = new transcript_pb.AcknowledgeTranscriptJobRequest();
    req.setName(job.name);

    this.client.acknowledgeTranscriptJob(
      req,
      new grpc.Metadata({ Authorization: `Bearer ${this.props.authCtx.accessToken}` }),
      (error: transcript_pb_grpc.ServiceError | null) => {
        if (error) {
          console.error(error.message);
        }
      },
    );
  };

  watchTranscriptJobs = () => {
    const req = new transcript_pb.WatchTranscriptJobsRequest();
    this.stream = this.client.watchTranscriptJobs(
      req,
      new grpc.Metadata({ Authorization: `Bearer ${this.props.authCtx.accessToken}` }),
    );
    this.stream.on('data', (msg: transcript_pb.WatchTranscriptJobsResponse) => {
      const jobs = msg
        .getJobsList()
        .map((job) => job.toObject())
        .filter((job) => !!job)
        .filter((job) => !job.transcriptJobAcknowledge)
        .filter((job) => !job.transcriptDeleted);

      this.updateSpinner(jobs);
    });
    this.stream.on('end', () => console.debug('stream ended'));
    this.stream.on('status', (status: Status) => {
      console.error('watchTranscriptJobs error', status);
      console.debug('stream status');
    });
  };

  getListOfErrorCodes = (jobs: transcript_pb.TranscriptJob.AsObject[]) => {
    const errorCodeList: number[] = [];
    jobs.forEach((job) => {
      if (job) {
        if (job.error) {
          if (job.error.message) {
            errorCodeList.push(job.error.code);
          }
        }
      }
    });
    return errorCodeList.filter((item, pos, self) => self.indexOf(item) === pos);
  };

  getListOfErrorMessages = (jobs: transcript_pb.TranscriptJob.AsObject[]) => {
    const errorMessageList: string[] = [];
    jobs.forEach((job) => {
      if (job) {
        if (job.error) {
          if (job.error.message) {
            errorMessageList.push(job.error.message);
          }
        }
      }
    });
    return errorMessageList.filter((item, pos, self) => self.indexOf(item) === pos);
  };

  getDetailsListTitle = () => {
    switch (this.state.clickedButton) {
      case 'errors':
        return 'Ný skjöl með villum';
      case 'success':
        return 'Ný skjöl';
      case 'ongoing':
        return 'Ný skjöl í vinnslu';
      default:
        return 'Skjöl';
    }
  };

  render() {
    const { ongoingList, successList, errorsList, listToDisplay, visible, clickedButton } = this.state;
    const ongoingListLength = ongoingList.length;
    const successListLength = successList.length;
    const errorListLength = errorsList.length;
    const displayList = listToDisplay;
    const detailsListTitle = this.getDetailsListTitle();

    return (
      <div className='spinner-segment'>
        <Modal
          width={1000}
          maskClosable={true}
          open={visible}
          title={
            <Title level={4} className='job-header'>
              {detailsListTitle}
            </Title>
          }
          onCancel={this.handleHideDetails}
          className='details-wrapper'
          footer={[
            <Button key='hide' type='primary' onClick={this.handleHideDetails}>
              Fela
              <ArrowRightOutlined />
            </Button>,
          ]}
        >
          <SpinnerDetails anonymousAccess={this.props.anonymousAccess} jobs={displayList} listClass={clickedButton} />

          {clickedButton === 'errors' && errorListLength >= 0 ? (
            <ErrorExplain
              messageList={this.getListOfErrorMessages(displayList)}
              codeList={this.getListOfErrorCodes(displayList)}
            ></ErrorExplain>
          ) : null}
        </Modal>

        <div className={'spinner-menu'}>
          <Space>
            <Badge count={errorListLength} color='red'>
              <Button danger className='errors-button spinner-button' onClick={() => this.handleShowDetails('errors')}>
                Nýjar villur
              </Button>
            </Badge>

            <Badge count={successListLength} color='green'>
              <Button className='success-button spinner-button' onClick={() => this.handleShowDetails('success')}>
                Ný tilbúin verkefni
              </Button>
            </Badge>

            <Badge count={ongoingListLength} color='blue'>
              <Button className='ongoing-button spinner-button' onClick={() => this.handleShowDetails('ongoing')}>
                <Spin spinning={!!ongoingListLength} size='small' style={{ marginRight: '8px' }} />
                Skrár í vinnslu
              </Button>
            </Badge>
          </Space>
        </div>
      </div>
    );
  }
}

type SpinnerDetailsProps = {
  anonymousAccess?: boolean;
  jobs?: transcript_pb.TranscriptJob.AsObject[];
  listClass?: string;
};

const SpinnerDetails: React.FC<SpinnerDetailsProps> = (props) => {
  const toDateTime = (seconds: number, nanos: number) => {
    const date = new Date(seconds * 1e3 + nanos * 1e-6);
    return dayjs(date).format('L LT');
  };

  const href = `/${props.anonymousAccess ? 'public-' : ''}`;
  if (!props.jobs) return null;

  const columns: ColumnsType<transcript_pb.TranscriptJob.AsObject> = [
    {
      title: 'Titill',
      dataIndex: ['transcriptMetadata', 'subject'],
      key: 'subject',
      width: '33%',
      className: 'subject',
      render: (_, job) => {
        const title = job.transcriptMetadata ? job.transcriptMetadata.subject : '';
        return props.listClass === 'success' && job.transcript ? (
          <Link to={`${href}${job.transcript}`}>{title}</Link>
        ) : (
          <p title={title}>{title}</p>
        );
      },
    },
    {
      title: 'Mál',
      dataIndex: ['transcriptMetadata', 'languageCode'],
      key: 'languageCode',
      width: '17%',
      className: 'language',
      render: (languageCode) => (languageCode ? <LanguageCode languageCode={languageCode} /> : null),
    },
    {
      title: 'Síðast uppfært',
      dataIndex: 'lastUpdatedTime',
      key: 'lastUpdatedTime',
      width: '17%',
      className: 'last-updatetime',
      render: (time) => (time ? toDateTime(time.seconds, time.nanos) : ''),
    },
    {
      title: 'Staða',
      key: 'state',
      width: '17%',
      className: 'progress',
      render: (_, job) => {
        const isError = job.state === transcript_pb.TranscriptJob.State.ERROR;
        const isSuccess = job.state === transcript_pb.TranscriptJob.State.SUCCESS;
        const percent = isError ? 100 : job.progressPercent;

        let status: 'exception' | 'success' | 'active' | 'normal' = 'active';
        if (isError) status = 'exception';
        else if (isSuccess) status = 'success';

        return (
          <Progress
            size='small'
            percent={percent}
            status={status}
            format={() => friendlyTranscriptJobState(job.state)} // Replaces 'label'
            title={`Í ástandi: ${job.state}. Lokið: ${job.progressPercent}%`}
          />
        );
      },
    },
    {
      title: 'Villa',
      dataIndex: 'error',
      key: 'error',
      width: '1S7%',
      className: 'error',
      render: (error) => {
        const message = error && props.listClass === 'errors' ? error.message : '';
        return <span title={message}>{message}</span>;
      },
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={props.jobs}
      rowKey='name'
      size='small'
      scroll={{ x: true }}
      pagination={false}
      className='details-table'
    />
  );
};

export default withAuth(JobsSpinner);
